'use client'

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { useEducationAdmin, EduClass, EduTeacher, EduStudent, EduProgress, EduGroup, EduGroupMember, EduStaffMember, EduCoTeacher } from '@/hooks/useEducationAdmin'
import { supabase } from '@/integrations/supabase/client'
import { StudentEducationLogs } from '@/components/admin/StudentEducationLogs'
import {
    GraduationCap, Users, BookOpen, Plus, Send, X,
    CheckCircle, AlertTriangle, ChevronRight,
    School, Star, BarChart3, UserPlus, Book, Settings, MoreHorizontal, Loader2,
    FileSpreadsheet, ClipboardList, ChevronDown, ArrowLeft, Image as ImageIcon, RotateCcw,
    Heart, Shield, MessageCircle, ArrowRight, Brain, Building, Activity, ShieldCheck,
    CheckCircle2, Sparkles, Quote, PhoneCall, HandHeart, Zap, ChevronLeft, Search, Briefcase, Mail, Filter,
    Crown
} from 'lucide-react'
import KnowledgeUploader from '@/components/education/KnowledgeUploader'
import CreateCourseModal, { CreateCoursePayload } from '@/components/education/CreateCourseModal'
import SyllabusGenerator, { SyllabusModule } from '@/components/education/SyllabusGenerator'
import { getEntitlements } from '@/utils/entitlements'
import { useFacilityBranding, useOrgVocab, SubdomainBrandingContext } from '@/contexts/SubdomainBrandingContext'
import { cleanCourseText } from '@/utils/textClean'
import LanguagePicker from '@/components/university/ui/LanguagePicker'

// ─── Design tokens ─────────────────────────────────────────────────────────────
// Aligned with the dark-first design system: dark navy/slate canvas, electric blue CTA, sage accents.
const EDU = {
    sage: '#4a7c5e',       // Sage brand primary
    sageLight: 'rgba(74, 124, 94, 0.2)',
    sageDark: '#2d5a3d',
    navy: '#f8fafc',       // Primary text in dark mode (bright white)
    navyMid: '#94a3b8',    // Secondary text
    cream: 'hsl(var(--background))', // Dark canvas background
    white: '#0f172a',      // Card surface in dark mode
    slate: '#94a3b8',
    muted: '#94a3b8',
    border: 'rgba(255,255,255,0.08)',
    gold: '#c9903a',
    emerald: '#059669',
    blue: '#38bdf8',       // Electric sky blue
    green: '#10b981',      // Original green
    amber: '#f59e0b',
    primary: '#0ea5e9',    // Electric blue
}

const btnGrad = `linear-gradient(135deg, ${EDU.sage}, ${EDU.sageDark})`
const heroGrad = `linear-gradient(135deg, #0f172a, #1e293b)`
const GOLD = '#D4AF37'
const GOLD_LIGHT = '#F4E08D'

// ─── Educational category type ─────────────────────────────────────────────────
interface EduCategory {
    id: string
    name: string
    icon: string
    educational_level: string | null
    parent_id: string | null
    slug: string | null
    target_demographics: any
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = EDU.blue }: {
    icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string
}) {
    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            style={{
                background: EDU.white, borderRadius: 16, padding: '18px 20px',
                border: `1px solid ${EDU.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color,
                }}>{icon}</div>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: EDU.muted }}>{label}</span>
            </div>
            <p style={{ fontSize: 28, fontWeight: 900, color: EDU.navy, margin: 0 }}>{value}</p>
            {sub && <p style={{ fontSize: 12, color: EDU.muted, margin: '4px 0 0' }}>{sub}</p>}
        </motion.div>
    )
}

function Toast({ text, type }: { text: string; type: 'success' | 'error' }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
            style={{ position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)', zIndex: 300 }}
        >
            <div style={{
                padding: '12px 24px', borderRadius: 50,
                background: type === 'success' ? EDU.green : '#dc2626',
                color: '#fff', display: 'flex', alignItems: 'center', gap: 10,
                fontWeight: 700, fontSize: 14, boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                whiteSpace: 'nowrap',
            }}>
                {type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                {text}
            </div>
        </motion.div>
    )
}

function Modal({ title, onClose, children, wide }: {
    title: string; onClose: () => void; children: React.ReactNode; wide?: boolean
}) {
    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
                position: 'fixed', inset: 0, zIndex: 200,
                display: 'flex', alignItems: 'flex-end',
                background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)',
                // On mobile: sheet from bottom. On desktop: centred.
            }}
            onClick={onClose}
        >
            <motion.div
                initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
                onClick={e => e.stopPropagation()}
                style={{
                    background: EDU.white,
                    borderRadius: '24px 24px 0 0',
                    padding: '28px 24px 40px',
                    width: '100%',
                    maxWidth: wide ? 560 : 460,
                    margin: '0 auto',
                    boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
                    position: 'relative',
                    maxHeight: '90dvh',
                    overflowY: 'auto',
                    // On desktop, convert to centred dialog
                    ...(typeof window !== 'undefined' && window.innerWidth >= 640 ? {
                        borderRadius: 24,
                        margin: 'auto',
                    } : {}),
                }}
            >
                {/* Drag handle on mobile */}
                <div style={{
                    width: 40, height: 4, borderRadius: 2, background: EDU.border,
                    margin: '0 auto 20px',
                }} />
                <button onClick={onClose} style={{
                    position: 'absolute', top: 20, right: 20,
                    background: EDU.cream, border: 'none', borderRadius: '50%',
                    width: 32, height: 32, cursor: 'pointer', color: EDU.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}><X size={16} /></button>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: EDU.navy, margin: '0 0 20px' }}>{title}</h3>
                {children}
            </motion.div>
        </motion.div>
    )
}

// ─── Generic invite form ────────────────────────────────────────────────────
function InviteForm({ label, fields, onSubmit, loading }: {
    label: string
    fields: { name: string; placeholder: string; type?: string }[]
    onSubmit: (vals: Record<string, string>) => Promise<void>
    loading: boolean
}) {
    const [vals, setVals] = useState<Record<string, string>>({})
    const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); await onSubmit(vals) }

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {fields.map(f => (
                <div key={f.name}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>{f.name}</label>
                    <input
                        type={f.type || 'text'} required
                        placeholder={f.placeholder} value={vals[f.name] || ''}
                        onChange={e => setVals(p => ({ ...p, [f.name]: e.target.value }))}
                        style={{ width: '100%', height: 48, padding: '0 14px', borderRadius: 12, border: `2px solid ${EDU.border}`, fontSize: 15, outline: 'none', boxSizing: 'border-box' }}
                    />
                </div>
            ))}
            <button type="submit" disabled={loading} style={{
                padding: '13px', borderRadius: 12, background: btnGrad, color: '#fff',
                border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: loading ? 0.6 : 1,
            }}>
                {loading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                {label}
            </button>
        </form>
    )
}

// ─── Bulk import modal ──────────────────────────────────────────────────────
function BulkImportModal({ onClose, onImport, loading }: {
    onClose: () => void
    onImport: (students: { name: string; email: string }[]) => Promise<void>
    loading: boolean
}) {
    const vocab = useOrgVocab()
    const [csv, setCsv] = useState('')
    const [preview, setPreview] = useState<{ name: string; email: string }[]>([])

    const parseCSV = (text: string) => {
        return text.split(/\r?\n/).filter(l => l.trim()).map(line => {
            const parts = line.split(/[,\t]/).map(s => s.trim())
            const email = parts.find(p => p.includes('@')) || ''
            const name = parts.find(p => !p.includes('@')) || ''
            return { email, name }
        }).filter(s => s.email.includes('@'))
    }

    useEffect(() => { setPreview(parseCSV(csv)) }, [csv])

    return (
        <Modal title={`Bulk ${vocab.learnerSingular} Import`} onClose={onClose}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <p style={{ fontSize: 13, color: EDU.muted, margin: 0 }}>
                    Paste {vocab.learnerPlural.toLowerCase()}, one per line — <b>Email, Name</b> or <b>Name, Email</b>.
                </p>
                <textarea value={csv} onChange={e => setCsv(e.target.value)}
                    placeholder={`alex@company.com, Alex Smith\njane@organisation.org, Jane Doe`}
                    rows={6}
                    style={{ width: '100%', padding: '12px', borderRadius: 12, border: `2px solid ${EDU.border}`, fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                />
                {preview.length > 0 && (
                    <div style={{ maxHeight: 120, overflowY: 'auto', background: EDU.cream, borderRadius: 10, padding: 10 }}>
                        <p style={{ fontSize: 11, fontWeight: 800, color: EDU.muted, textTransform: 'uppercase', marginBottom: 6 }}>Preview — {preview.length} {vocab.learnerPlural.toLowerCase()}</p>
                        {preview.map((p, i) => (
                            <div key={i} style={{ fontSize: 12, color: EDU.navy, display: 'flex', gap: 8, marginBottom: 2 }}>
                                <span style={{ fontWeight: 700 }}>{p.email}</span>
                                <span style={{ color: EDU.muted }}>{p.name}</span>
                            </div>
                        ))}
                    </div>
                )}
                <button onClick={() => onImport(preview)} disabled={loading || preview.length === 0}
                    style={{
                        padding: '13px', borderRadius: 12, background: btnGrad, color: '#fff', border: 'none',
                        fontWeight: 700, fontSize: 15, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        opacity: (loading || preview.length === 0) ? 0.6 : 1,
                    }}>
                    {loading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <ClipboardList size={18} />}
                    Invite {preview.length || ''} {vocab.learnerPlural}
                </button>
            </div>
        </Modal>
    )
}

// ─── Class Card ─────────────────────────────────────────────────────────────
function ClassCard({ cls, onClick }: { cls: EduClass; onClick: () => void }) {
    const { t } = useTranslation()
    return (
        <motion.button
            layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            whileHover={{ y: -2, boxShadow: `0 12px 40px ${EDU.sage}18` }}
            onClick={onClick}
            style={{
                background: EDU.white, borderRadius: 18, padding: 20,
                border: `1px solid ${EDU.border}`, cursor: 'pointer', textAlign: 'left', width: '100%',
            }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: heroGrad, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <BookOpen size={20} color="#fff" />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {cls.is_published ? (
                        cls.enrollment_mode === 'targeted' ? (
                            <span style={{
                                fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
                                background: 'rgba(168,85,247,0.12)', color: '#a855f7', padding: '3px 9px', borderRadius: 20,
                                display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid rgba(168,85,247,0.25)'
                            }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#a855f7' }} />
                                Live — {cls.student_count ?? 0} assigned learner{(cls.student_count ?? 0) === 1 ? '' : 's'}
                            </span>
                        ) : (
                            <span style={{
                                fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
                                background: 'rgba(16,185,129,0.12)', color: '#10b981', padding: '3px 9px', borderRadius: 20,
                                display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid rgba(16,185,129,0.25)'
                            }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
                                Live — entire hub
                            </span>
                        )
                    ) : (
                        <span style={{
                            fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
                            background: 'rgba(148,163,184,0.12)', color: '#94a3b8', padding: '3px 9px', borderRadius: 20,
                            display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid rgba(148,163,184,0.2)'
                        }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#94a3b8' }} />
                            Draft — invisible to learners
                        </span>
                    )}
                </div>
            </div>
            <h3 style={{ fontWeight: 800, color: EDU.navy, margin: '0 0 4px', fontSize: 16 }}>{cleanCourseText(cls.name)}</h3>
            {cls.teacher_name && (
                <p style={{ fontSize: 13, color: EDU.muted, margin: '0 0 12px' }}>👩‍🏫 {cls.teacher_name}</p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: EDU.muted }}>{cls.target_age ? `Age ${cls.target_age}` : 'All ages'}</span>
                <ChevronRight size={16} color={EDU.muted} />
            </div>
        </motion.button>
    )
}

// ─── Class Detail Panel ─────────────────────────────────────────────────────
function ClassDetail({
    cls, students, progress, onBack, onInviteStudent, onBulkImport, inviteLoading, onShowToast, fetchStudentLogs, navigate, canPublishCommunity,
    onSetEnrollmentMode, onAssignStudents, onSharing, onRemoveStudentEnrollment, getEnrolledStudents, onPublishCourse, onResendStudentInvite,
    onGroups, onMoveStudentToGroup, onHandoffStudent
}: {
    cls: EduClass
    students: EduStudent[]
    progress: EduProgress[]
    onBack: () => void
    onInviteStudent: (email: string, name: string) => Promise<void>
    onBulkImport: (students: { name: string; email: string }[]) => Promise<void>
    inviteLoading: boolean
    onShowToast: (text: string, type: 'success' | 'error') => void
    fetchStudentLogs: (studentId: string, classId?: string) => Promise<any[]>
    navigate: (path: string) => void
    canPublishCommunity: boolean
    onSetEnrollmentMode?: (classId: string, mode: 'all' | 'targeted') => Promise<void>
    onAssignStudents?: (classId: string, studentIds: string[]) => Promise<void>
    onRemoveStudentEnrollment?: (classId: string, studentId: string) => Promise<void>
    getEnrolledStudents?: (classId: string) => Promise<string[]>
    onPublishCourse?: (classId: string, publish: boolean) => Promise<any>
    onResendStudentInvite?: (email: string, name?: string) => Promise<void>
    onGroups?: { list: EduGroup[]; members: EduGroupMember[] }
    onMoveStudentToGroup?: (studentId: string, groupId: string | null) => Promise<void>
    onHandoffStudent?: (studentId: string, studentName: string) => void
    onSharing?: {
        staff: EduStaffMember[]
        coTeachers: EduCoTeacher[]
        onAdd: (classId: string, teacherId: string) => Promise<void>
        onRemove: (classId: string, teacherId: string) => Promise<void>
        onTransfer: (classId: string, toTeacherId: string, fromTeacherId: string, reason?: string) => Promise<void>
    }
}) {
    const { t } = useTranslation()
    const [tab, setTab] = useState<'students' | 'knowledge' | 'syllabus' | 'media' | 'settings'>('students')
    const [showInvite, setShowInvite] = useState(false)
    const [showBulk, setShowBulk] = useState(false)
    const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null)
    const [studentLogs, setStudentLogs] = useState<any[]>([])
    const [logsLoading, setLogsLoading] = useState(false)
    const [feedbackDraft, setFeedbackDraft] = useState<Record<string, string>>({})
    const [feedbackType, setFeedbackType] = useState<Record<string, string>>({})
    const [overrideDraft, setOverrideDraft] = useState<Record<string, string>>({})
    const [overrideReason, setOverrideReason] = useState<Record<string, string>>({})
    const [enrolledStudentIds, setEnrolledStudentIds] = useState<string[]>([])
    const [loadingEnrolled, setLoadingEnrolled] = useState(false)
    const [showTargetedPicker, setShowTargetedPicker] = useState(false)
    const [pickerSearch, setPickerSearch] = useState('')
    const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([])
    const [savingTargeted, setSavingTargeted] = useState(false)
    const [publishingCourse, setPublishingCourse] = useState(false)
    const { user } = useAuth()
    const vocab = useOrgVocab()

    useEffect(() => {
        if (!getEnrolledStudents || !cls.id) return
        setLoadingEnrolled(true)
        getEnrolledStudents(cls.id)
            .then(ids => {
                setEnrolledStudentIds(ids)
                setSelectedTargetIds(ids)
            })
            .catch(() => {})
            .finally(() => setLoadingEnrolled(false))
    }, [cls.id, getEnrolledStudents])

    const handleSaveTargetedEnrollment = async () => {
        if (!onAssignStudents || !onRemoveStudentEnrollment || !cls.id) return
        setSavingTargeted(true)
        try {
            const currentSet = new Set(enrolledStudentIds)
            const selectedSet = new Set(selectedTargetIds)
            const toRemove = [...currentSet].filter(id => !selectedSet.has(id))
            const toAdd = [...selectedSet].filter(id => !currentSet.has(id))

            for (const remId of toRemove) {
                await onRemoveStudentEnrollment(cls.id, remId)
            }
            if (toAdd.length > 0) {
                await onAssignStudents(cls.id, toAdd)
            }
            setEnrolledStudentIds(selectedTargetIds)
            setShowTargetedPicker(false)
            if (!cls.is_published) {
                onShowToast(`Targeted roster saved (${selectedTargetIds.length} assigned). Course remains in Draft until published.`, 'success')
            } else {
                onShowToast(`Targeted ${vocab.learnerPlural.toLowerCase()} updated successfully!`, 'success')
            }
        } catch (err: any) {
            onShowToast('Failed to update assignments: ' + (err.message || ''), 'error')
        } finally {
            setSavingTargeted(false)
        }
    }

    const handleExpandStudent = async (studentId: string) => {
        if (expandedStudentId === studentId) {
            setExpandedStudentId(null)
            return
        }
        setExpandedStudentId(studentId)
        setLogsLoading(true)
        try {
            const res = await fetchStudentLogs(studentId, cls.id)
            setStudentLogs(res)
        } catch {
            onShowToast(`Failed to fetch ${vocab.learnerSingular.toLowerCase()} progress details`, 'error')
        } finally {
            setLogsLoading(false)
        }
    }

    const existingPlan = cls.module_plan && Array.isArray(cls.module_plan) && cls.module_plan.length > 0
        ? (cls.module_plan as unknown as SyllabusModule[])
        : null

    const tabs = [
        { id: 'students' as const, label: vocab.learnerTabLabel, icon: <Users size={13} /> },
        { id: 'knowledge' as const, label: 'Knowledge', icon: <Book size={13} /> },
        { id: 'syllabus' as const, label: 'Syllabus', icon: <BookOpen size={13} /> },
        { id: 'media' as const, label: 'Media', icon: <ImageIcon size={13} /> },
        { id: 'settings' as const, label: 'Settings', icon: <Settings size={13} /> },
    ]

    return (
        <div style={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            <button onClick={onBack}
                style={{ display: 'flex', alignItems: 'center', gap: 6, color: EDU.muted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, fontSize: 14 }}>
                <ArrowLeft size={16} /> Back to {vocab.coursePlural}
            </button>

            {/* Class hero */}
            <div style={{
                background: heroGrad, borderRadius: 18, padding: '22px 20px 18px',
                marginBottom: 20, color: '#fff',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div style={{ width: 46, height: 46, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <BookOpen size={22} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <h2 style={{ fontWeight: 900, fontSize: 'clamp(15px,4vw,18px)', margin: 0, color: '#fff', lineHeight: 1.2 }}>{cleanCourseText(cls.name)}</h2>
                        {cls.description && <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: 13, color: '#fff', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{cleanCourseText(cls.description)}</p>}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, opacity: 0.8, flexWrap: 'wrap' }}>
                    <span>👩‍🏫 {cls.teacher_name || 'No teacher'}</span>
                    <span>👩‍🎓 {cls.student_count ?? 0} students</span>
                    {cls.target_age && <span>🎂 Age {cls.target_age}</span>}
                </div>
            </div>

            {/* Tab bar — scrollable on mobile, stays within viewport width */}
            <div style={{
                overflowX: 'auto', paddingBottom: 4, marginBottom: 16,
                msOverflowStyle: 'none', scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
                width: '100%', boxSizing: 'border-box',
            }}>
                <div style={{
                    display: 'inline-flex', gap: 2, background: EDU.cream, borderRadius: 12, padding: 4,
                    minWidth: '100%',
                }}>
                    {tabs.map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            style={{
                                flexShrink: 0, padding: '9px clamp(8px,2.5vw,14px)', borderRadius: 10, border: 'none', cursor: 'pointer',
                                background: tab === t.id ? EDU.white : 'transparent',
                                color: tab === t.id ? EDU.blue : EDU.muted,
                                fontWeight: tab === t.id ? 700 : 500, fontSize: 13,
                                boxShadow: tab === t.id ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                                display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s',
                                whiteSpace: 'nowrap'
                            }}>
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Students tab */}
            {tab === 'students' && (
                <div>
                    {cls.enrollment_mode === 'targeted' && (
                        <div style={{
                            padding: '12px 14px', borderRadius: 12, background: 'rgba(245,158,11,0.08)',
                            border: '1px solid rgba(245,158,11,0.25)', marginBottom: 14,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10
                        }}>
                            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                <span style={{ fontWeight: 800, color: '#d97706', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5 }}>🎯 Targeted Enrollment</span>
                                <p style={{ fontSize: 11.5, color: EDU.muted, margin: '3px 0 0', lineHeight: 1.4 }}>
                                    {enrolledStudentIds.length} {vocab.learnerPlural.toLowerCase()} assigned — only they see this course.
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setSelectedTargetIds(enrolledStudentIds)
                                    setShowTargetedPicker(true)
                                }}
                                style={{
                                    padding: '7px 12px', borderRadius: 9, background: '#d97706', color: '#fff',
                                    border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                                    flexShrink: 0, whiteSpace: 'nowrap'
                                }}
                            >
                                <Users size={13} /> Manage
                            </button>
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                        {cls.enrollment_mode === 'targeted' && (
                            <button
                                onClick={() => {
                                    setSelectedTargetIds(enrolledStudentIds)
                                    setShowTargetedPicker(true)
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                                    background: 'rgba(245,158,11,0.12)', color: '#d97706', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10,
                                    fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                                }}
                            >
                                <Users size={13} /> Assign ({enrolledStudentIds.length})
                            </button>
                        )}
                        <button onClick={() => setShowBulk(true)} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                            background: EDU.white, color: EDU.blue, border: `1px solid ${EDU.blue}40`, borderRadius: 10,
                            fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                            <FileSpreadsheet size={13} /> Bulk CSV
                        </button>
                        <button onClick={() => setShowInvite(true)} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                            background: btnGrad, color: '#fff', border: 'none', borderRadius: 10,
                            fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                            <UserPlus size={13} /> Invite
                        </button>
                    </div>

                    {students.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '36px 20px', background: EDU.white, borderRadius: 14, border: `1px solid ${EDU.border}` }}>
                            <Users size={28} style={{ margin: '0 auto 10px', color: EDU.muted, display: 'block' }} />
                            <p style={{ color: EDU.muted, margin: 0, fontSize: 14 }}>No {vocab.learnerPlural.toLowerCase()} yet. Invite some above.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {students.map(s => {
                                const prog = progress.find(p => p.user_id === s.id && p.category_id === cls.category_id)
                                const mastery = Math.round(prog?.mastery_score ?? 0)
                                const mastered = mastery >= 90

                                return (
                                    <div key={s.id} style={{
                                        background: EDU.white, borderRadius: 16, overflow: 'hidden',
                                        border: `1px solid ${expandedStudentId === s.id ? EDU.blue : EDU.border}`,
                                        boxShadow: expandedStudentId === s.id ? `0 4px 20px ${EDU.blue}12` : 'none',
                                        transition: 'all 0.2s'
                                    }}>
                                        <div
                                            onClick={() => handleExpandStudent(s.id)}
                                            style={{
                                                padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                cursor: 'pointer', flexWrap: 'wrap', gap: 10
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 180px', minWidth: 0 }}>
                                                <div style={{
                                                    width: 40, height: 40, borderRadius: '50%',
                                                    background: mastered ? `${EDU.sage}15` : `${EDU.blue}12`,
                                                    color: mastered ? EDU.sage : EDU.blue,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontWeight: 900, fontSize: 16, flexShrink: 0,
                                                }}>
                                                    {s.name ? s.name[0].toUpperCase() : '?'}
                                                </div>
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <p style={{ fontWeight: 800, color: EDU.navy, margin: 0, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name || 'Unnamed Student'}</p>
                                                    <p style={{ fontSize: 11, color: EDU.muted, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.email}</p>
                                                </div>
                                            </div>

                                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
                                                {onGroups && onMoveStudentToGroup && (
                                                    <select
                                                        value={onGroups.members.find(m => m.student_id === s.id && onGroups.list.some(g => g.id === m.group_id))?.group_id || ''}
                                                        onClick={e => e.stopPropagation()}
                                                        onChange={async e => {
                                                            e.stopPropagation()
                                                            await onMoveStudentToGroup(s.id, e.target.value || null)
                                                        }}
                                                        style={{ fontSize: 11, padding: '4px 8px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: EDU.white, color: EDU.navy, cursor: 'pointer' }}
                                                        title="Assign to one of your groups"
                                                    >
                                                        <option value="">No group</option>
                                                        {onGroups.list.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                                    </select>
                                                )}
                                                {onHandoffStudent && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); onHandoffStudent(s.id, s.name || s.email) }}
                                                        title="Hand off to another teacher or release"
                                                        style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px',
                                                            borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'rgba(168,85,247,0.1)',
                                                            color: '#a855f7', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                        }}
                                                    >
                                                        <ArrowRight size={12} /> Hand off
                                                    </button>
                                                )}
                                                {onResendStudentInvite && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            onResendStudentInvite(s.email, s.name || undefined)
                                                        }}
                                                        title="Resend invitation email"
                                                        style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px',
                                                            borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'rgba(56,189,248,0.1)',
                                                            color: EDU.blue, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                                        }}
                                                    >
                                                        <Mail size={12} /> Resend
                                                    </button>
                                                )}
                                                {cls.enrollment_mode === 'targeted' && (
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 20,
                                                        background: enrolledStudentIds.includes(s.id) ? 'rgba(245,158,11,0.15)' : 'rgba(148,163,184,0.12)',
                                                        color: enrolledStudentIds.includes(s.id) ? '#d97706' : '#94a3b8',
                                                    }}>
                                                        {enrolledStudentIds.includes(s.id) ? '🎯 Assigned' : 'Not Assigned'}
                                                    </span>
                                                )}
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: 18, fontWeight: 900, color: mastered ? EDU.sage : EDU.navy }}>{mastery}%</div>
                                                    <div style={{ fontSize: 9, color: EDU.muted, fontWeight: 800, textTransform: 'uppercase' }}>Mastery</div>
                                                </div>
                                                <ChevronDown size={18} color={EDU.muted} style={{ transform: expandedStudentId === s.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                            </div>
                                        </div>

                                        <AnimatePresence>
                                            {expandedStudentId === s.id && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    style={{ overflow: 'hidden', background: '#fcfcfe', borderTop: `1px solid ${EDU.border}` }}
                                                >
                                                    <div style={{ padding: '20px clamp(12px,3vw,24px) 24px clamp(16px,5vw,72px)' }}>
                                                        <h5 style={{ fontSize: 11, fontWeight: 800, color: EDU.muted, textTransform: 'uppercase', marginBottom: 16, letterSpacing: '0.05em' }}>{t('education.student_learning_profile')}</h5>

                                                        {logsLoading ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: EDU.muted, fontSize: 13 }}>
                                                                <Loader2 size={16} className="animate-spin" /> Fetching latest insights...
                                                            </div>
                                                        ) : studentLogs.length === 0 ? (
                                                            <p style={{ fontSize: 13, color: EDU.muted, fontStyle: 'italic' }}>{t('education.no_detailed_lesson_logs_available_yet')}</p>
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                                                {studentLogs.slice(0, 5).map((log, i) => (
                                                                    <div key={i} style={{ position: 'relative', paddingLeft: 16, borderLeft: `2px solid ${EDU.border}` }}>
                                                                        <div style={{ position: 'absolute', left: -5, top: 0, width: 8, height: 8, borderRadius: '50%', background: log.metadata?.score !== undefined ? (log.metadata.score >= 80 ? EDU.sage : EDU.gold) : EDU.blue }} />
                                                                        <div style={{ fontWeight: 700, fontSize: 13, color: EDU.navy, marginBottom: 4 }}>
                                                                            L{log.lesson_number} Completion {log.metadata?.score !== undefined && `— ${log.metadata.score}% Quiz Score`}
                                                                        </div>

                                                                        {log.metadata?.concept_understood && (
                                                                            <div style={{ fontSize: 12, color: EDU.slate, marginBottom: 4 }}>
                                                                                <span style={{ fontWeight: 800, color: EDU.sage }}>✓</span> {log.metadata.concept_understood}
                                                                            </div>
                                                                        )}
                                                                        {log.metadata?.weak_concept_flagged && (
                                                                            <div style={{ fontSize: 12, color: EDU.slate, marginBottom: 4 }}>
                                                                                <span style={{ fontWeight: 800, color: '#dc2626' }}>!</span> AI Flagged: {log.metadata.weak_concept_flagged}
                                                                            </div>
                                                                        )}
                                                                        {log.metadata?.weak_areas && log.metadata.weak_areas.length > 0 && (
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                                                                                {log.metadata.weak_areas.map((wa: string) => (
                                                                                    <span key={wa} style={{ background: '#fef2f2', color: '#dc2626', fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700, border: '1px solid #fee2e2' }}>{wa}</span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* ── Teacher feedback & grade override ────────────────────── */}
                                                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${EDU.border}` }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                                <h6 style={{ fontSize: 10, fontWeight: 800, color: EDU.muted, textTransform: 'uppercase', margin: 0, letterSpacing: '0.05em' }}>{t('education.teacher_assessment_feedback')}</h6>
                                                                <select
                                                                    value={feedbackType[s.id] || 'lesson_comment'}
                                                                    onChange={e => setFeedbackType(p => ({ ...p, [s.id]: e.target.value }))}
                                                                    style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: `1px solid ${EDU.border}`, background: EDU.white, color: EDU.navy }}
                                                                >
                                                                    <option value="lesson_comment">{t('education.lesson_comment')}</option>
                                                                    <option value="encouragement">Encouragement</option>
                                                                    <option value="improvement_note">{t('education.improvement_note')}</option>
                                                                </select>
                                                            </div>
                                                            <textarea
                                                                value={feedbackDraft[s.id] || ''}
                                                                onChange={e => setFeedbackDraft(p => ({ ...p, [s.id]: e.target.value }))}
                                                                placeholder={t('education.write_note_or_encouragement_for_student')}
                                                                rows={2}
                                                                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, fontSize: 12, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }}
                                                            />
                                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <button
                                                                    onClick={async () => {
                                                                        const body = feedbackDraft[s.id]?.trim()
                                                                        if (!body) return
                                                                        const { error } = await supabase.from('teacher_feedback' as any).insert({
                                                                            student_id: s.id,
                                                                            class_id: cls.id,
                                                                            author_id: user!.id,
                                                                            feedback_type: feedbackType[s.id] || 'lesson_comment',
                                                                            targets_lesson: false,
                                                                            body,
                                                                            visible_to_student: true,
                                                                        })
                                                                        if (!error) {
                                                                            setFeedbackDraft(p => ({ ...p, [s.id]: '' }))
                                                                            onShowToast('Feedback saved', 'success')
                                                                        } else { onShowToast('Failed to save feedback: ' + (error?.message || ''), 'error') }
                                                                    }}
                                                                    disabled={!feedbackDraft[s.id]?.trim()}
                                                                    style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: EDU.primary, color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', opacity: feedbackDraft[s.id]?.trim() ? 1 : 0.5 }}>
                                                                    Save Feedback
                                                                </button>
                                                                {/* Grade override — on the most recent graded lesson */}
                                                                {(() => {
                                                                    const lastLog = studentLogs.find(l => l.metadata?.score !== undefined)
                                                                    if (!lastLog) return null
                                                                    return (
                                                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
                                                                            <span style={{ fontSize: 11, color: EDU.muted, fontWeight: 600 }}>AI: {lastLog.metadata.score}% →</span>
                                                                            <input
                                                                                type="number" min={0} max={100}
                                                                                placeholder={String(lastLog.metadata.score)}
                                                                                value={overrideDraft[s.id] || ''}
                                                                                onChange={e => setOverrideDraft(p => ({ ...p, [s.id]: e.target.value }))}
                                                                                style={{ width: 54, padding: '5px 6px', borderRadius: 6, border: `1px solid ${EDU.border}`, fontSize: 11, textAlign: 'center' }}
                                                                            />
                                                                            <input
                                                                                type="text" placeholder={t('education.override_reason')}
                                                                                value={overrideReason[s.id] || ''}
                                                                                onChange={e => setOverrideReason(p => ({ ...p, [s.id]: e.target.value }))}
                                                                                style={{ width: 140, padding: '5px 8px', borderRadius: 6, border: `1px solid ${EDU.border}`, fontSize: 11 }}
                                                                            />
                                                                            <button
                                                                                onClick={async () => {
                                                                                    const newScore = parseInt(overrideDraft[s.id] || '')
                                                                                    if (isNaN(newScore)) return
                                                                                    const { error } = await supabase.from('teacher_feedback' as any).insert({
                                                                                        student_id: s.id,
                                                                                        class_id: cls.id,
                                                                                        author_id: user!.id,
                                                                                        feedback_type: 'grade_override',
                                                                                        original_score: lastLog.metadata.score,
                                                                                        override_score: newScore,
                                                                                        override_reason: overrideReason[s.id]?.trim() || null,
                                                                                        targets_lesson: true,
                                                                                        lesson_unit: lastLog.unit_number,
                                                                                        lesson_number: lastLog.lesson_number,
                                                                                        body: null,
                                                                                        visible_to_student: true,
                                                                                    })
                                                                                    if (!error) {
                                                                                        setOverrideDraft(p => ({ ...p, [s.id]: '' }))
                                                                                        setOverrideReason(p => ({ ...p, [s.id]: '' }))
                                                                                        onShowToast('Grade override recorded (AI grade preserved)', 'success')
                                                                                    } else { onShowToast('Failed to save override: ' + (error?.message || ''), 'error') }
                                                                                }}
                                                                                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #D4AF37', background: '#FBF6EA', color: '#A8842C', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
                                                                                Override Grade
                                                                            </button>
                                                                        </div>
                                                                    )
                                                                })()}
                                                            </div>
                                                        </div>

                                                        <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${EDU.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <button
                                                                onClick={() => navigate(`/record/${s.id}`)}
                                                                style={{ background: 'none', border: 'none', padding: 0, color: EDU.blue, fontWeight: 800, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                                                                View Full Transcript & Records →
                                                            </button>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    <AnimatePresence>
                        {showInvite && (
                            <Modal title={vocab.inviteLearnerTitle} onClose={() => setShowInvite(false)}>
                                <InviteForm
                                    label={t('education.send_invite')}
                                    fields={[
                                        { name: 'Name', placeholder: `${vocab.learnerSingular} name` },
                                        { name: 'Email', placeholder: `${vocab.learnerSingular.toLowerCase().replace(/ /g, '.')}@example.com`, type: 'email' },
                                    ]}
                                    onSubmit={async (v) => {
                                        await onInviteStudent(v['Email'], v['Name'])
                                        setShowInvite(false)
                                    }}
                                    loading={inviteLoading}
                                />
                            </Modal>
                        )}
                        {showBulk && (
                            <BulkImportModal
                                onClose={() => setShowBulk(false)}
                                loading={inviteLoading}
                                onImport={async (s) => { await onBulkImport(s); setShowBulk(false) }}
                            />
                        )}
                        {showTargetedPicker && (
                            <Modal title={`Assign ${vocab.learnerPlural} to ${cls.name}`} onClose={() => setShowTargetedPicker(false)}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <p style={{ fontSize: 12, color: EDU.muted, margin: 0 }}>
                                        Targeted course · {selectedTargetIds.length} of {students.length} selected
                                    </p>

                                    <input
                                        type="text"
                                        placeholder={`Search ${vocab.learnerPlural.toLowerCase()}…`}
                                        value={pickerSearch}
                                        onChange={e => setPickerSearch(e.target.value)}
                                        style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: `1px solid ${EDU.border}`, background: EDU.white, color: EDU.navy, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                                    />

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTargetIds(students.map(s => s.id))}
                                            style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.navy, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedTargetIds([])}
                                            style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.muted, fontSize: 11, cursor: 'pointer' }}
                                        >
                                            Clear All
                                        </button>
                                    </div>

                                    <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}>
                                        {students
                                            .filter(s => !pickerSearch.trim() || (s.name || '').toLowerCase().includes(pickerSearch.toLowerCase()) || s.email.toLowerCase().includes(pickerSearch.toLowerCase()))
                                            .map(s => {
                                                const isChecked = selectedTargetIds.includes(s.id)
                                                return (
                                                    <label
                                                        key={s.id}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                                                            borderRadius: 10, cursor: 'pointer',
                                                            background: isChecked ? 'rgba(245,158,11,0.1)' : EDU.white,
                                                            border: `1px solid ${isChecked ? 'rgba(245,158,11,0.3)' : EDU.border}`,
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={e => {
                                                                if (e.target.checked) {
                                                                    setSelectedTargetIds(prev => [...prev, s.id])
                                                                } else {
                                                                    setSelectedTargetIds(prev => prev.filter(id => id !== s.id))
                                                                }
                                                            }}
                                                            style={{ width: 16, height: 16, accentColor: '#d97706', cursor: 'pointer' }}
                                                        />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: EDU.navy }}>{s.name || 'Unnamed learner'}</div>
                                                            <div style={{ fontSize: 11, color: EDU.muted }}>{s.email}</div>
                                                        </div>
                                                    </label>
                                                )
                                            })}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10, paddingTop: 12, borderTop: `1px solid ${EDU.border}` }}>
                                        <button
                                            type="button"
                                            onClick={() => setShowTargetedPicker(false)}
                                            style={{ padding: '8px 16px', borderRadius: 10, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSaveTargetedEnrollment}
                                            disabled={savingTargeted}
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 20px',
                                                borderRadius: 10, border: 'none', background: '#d97706', color: '#fff',
                                                fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: savingTargeted ? 0.6 : 1,
                                            }}
                                        >
                                            {savingTargeted ? <Loader2 size={14} className="animate-spin" /> : null}
                                            Save Assignments
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        )}
                    </AnimatePresence>
                </div>
            )}

            {tab === 'knowledge' && (
                <div style={{ background: '#0d1527', borderRadius: 14, padding: 'clamp(12px, 3vw, 20px)', border: '1px solid rgba(255,255,255,0.08)', width: '100%', boxSizing: 'border-box' }}>
                    <KnowledgeUploader classId={cls.id} />
                </div>
            )}

            {tab === 'syllabus' && (
                <div style={{ background: '#0d1527', borderRadius: 14, padding: 'clamp(12px, 3vw, 20px)', border: '1px solid rgba(255,255,255,0.08)', width: '100%', boxSizing: 'border-box' }}>
                    <SyllabusGenerator
                        classId={cls.id}
                        targetAge={cls.target_age}
                        subjectName={cls.name}
                        existingPlan={existingPlan}
                        onSyllabusSaved={() => {
                            onShowToast('Syllabus published successfully!', 'success')
                        }}
                        mode="syllabus"
                    />
                </div>
            )}

            {tab === 'media' && (
                <div style={{ background: '#0d1527', borderRadius: 14, padding: 'clamp(12px, 3vw, 20px)', border: '1px solid rgba(255,255,255,0.08)', width: '100%', boxSizing: 'border-box' }}>
                    <SyllabusGenerator
                        classId={cls.id}
                        targetAge={cls.target_age}
                        subjectName={cls.name}
                        existingPlan={existingPlan}
                        onSyllabusSaved={() => {
                            onShowToast('Media settings saved!', 'success')
                        }}
                        mode="media"
                        onGoToSyllabus={() => setTab('syllabus')}
                    />
                </div>
            )}

            {tab === 'settings' && (
                <div style={{ background: EDU.white, borderRadius: 14, padding: 'clamp(12px, 3vw, 20px)', border: `1px solid ${EDU.border}`, width: '100%', boxSizing: 'border-box' }}>
                    <h4 style={{ fontWeight: 700, color: EDU.navy, margin: '0 0 16px' }}>{t('education.class_settings')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {[
                            { label: 'Name', value: cls.name },
                            { label: 'Target Age', value: cls.target_age ? `${cls.target_age}` : '—' },
                            { label: 'Description', value: cls.description || '—' },
                        ].map(row => (
                            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: `1px solid ${EDU.border}` }}>
                                <span style={{ fontSize: 14, color: EDU.muted }}>{row.label}</span>
                                <span style={{ fontSize: 14, fontWeight: 600, color: EDU.navy, textAlign: 'right', maxWidth: '60%' }}>{row.value}</span>
                            </div>
                        ))}
                    </div>

                    {/* Course Hub Status (Publish / Unpublish) */}
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${EDU.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <div style={{ fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>Course Status on Hub</div>
                            <div style={{ fontSize: 11.5, color: EDU.muted }}>
                                {cls.is_published
                                    ? (cls.enrollment_mode === 'targeted'
                                        ? `Live: visible to ${enrolledStudentIds.length} assigned ${vocab.learnerPlural.toLowerCase()} in their portal.`
                                        : `Live: visible to all active ${vocab.learnerPlural.toLowerCase()} in your hub.`)
                                    : `Draft: hidden from ${vocab.learnerPlural.toLowerCase()}. Visible only to educators and admins.`}
                            </div>
                        </div>
                        {onPublishCourse && (
                            <button
                                type="button"
                                disabled={publishingCourse}
                                onClick={async () => {
                                    if (!cls.is_published && cls.enrollment_mode === 'targeted' && enrolledStudentIds.length === 0) {
                                        onShowToast(`Cannot publish a targeted course with 0 ${vocab.learnerPlural.toLowerCase()} assigned. Add ${vocab.learnerPlural.toLowerCase()} first or switch to Entire Hub.`, 'error')
                                        return
                                    }
                                    const nextState = !cls.is_published
                                    setPublishingCourse(true)
                                    try {
                                        await onPublishCourse(cls.id, nextState)
                                        cls.is_published = nextState
                                        onShowToast(nextState ? 'Course is now Live on the hub!' : 'Course moved back to Draft.', 'success')
                                    } catch (err: any) {
                                        onShowToast('Failed to update course status: ' + (err.message || ''), 'error')
                                    } finally {
                                        setPublishingCourse(false)
                                    }
                                }}
                                style={{
                                    padding: '8px 16px', borderRadius: 10, border: 'none', cursor: publishingCourse ? 'default' : 'pointer',
                                    fontWeight: 700, fontSize: 12,
                                    background: cls.is_published ? '#fee2e2' : 'rgba(16,185,129,0.15)',
                                    color: cls.is_published ? '#b91c1c' : '#10b981',
                                    opacity: publishingCourse ? 0.6 : 1,
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                }}
                            >
                                {publishingCourse ? <Loader2 size={13} className="animate-spin" /> : null}
                                {cls.is_published ? 'Unpublish (Move to Draft)' : '🚀 Publish Course to Hub'}
                            </button>
                        )}
                    </div>

                    {/* Audience & Enrollment Mode Setting */}
                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${EDU.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <div style={{ fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>Audience &amp; Enrollment Mode</div>
                            <div style={{ fontSize: 11.5, color: EDU.muted }}>
                                {cls.enrollment_mode === 'targeted'
                                    ? `Restricted: only ${enrolledStudentIds.length} assigned ${vocab.learnerPlural.toLowerCase()} can access this course.`
                                    : `Open: all active ${vocab.learnerPlural.toLowerCase()} in your hub can access this course when published.`}
                            </div>
                        </div>
                        {onSetEnrollmentMode && (
                            <button
                                onClick={async () => {
                                    const nextMode = cls.enrollment_mode === 'targeted' ? 'all' : 'targeted'
                                    try {
                                        await onSetEnrollmentMode(cls.id, nextMode)
                                        cls.enrollment_mode = nextMode
                                        onShowToast(`Switched to: ${nextMode === 'targeted' ? 'Targeted Learners' : 'Entire Hub'}`, 'success')
                                    } catch (err: any) {
                                        onShowToast('Failed to change mode: ' + (err.message || ''), 'error')
                                    }
                                }}
                                style={{
                                    padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                                    fontWeight: 700, fontSize: 12,
                                    background: cls.enrollment_mode === 'targeted' ? '#fef3c7' : `${EDU.sage}15`,
                                    color: cls.enrollment_mode === 'targeted' ? '#b45309' : EDU.sage,
                                }}
                            >
                                {cls.enrollment_mode === 'targeted' ? '🎯 Targeted (Switch to All)' : '👥 Entire Hub (Switch to Targeted)'}
                            </button>
                        )}
                    </div>

                    {/* Sharing & Ownership — co-teachers + ownership transfer.
                        Owner/admin only; RLS enforces the same boundary server-side. */}
                    {onSharing && (canPublishCommunity || cls.teacher_id === user?.id) && (() => {
                        const sharing = onSharing
                        const classCoTeachers = sharing.coTeachers.filter(ct => ct.class_id === cls.id)
                        const shareable = sharing.staff.filter(st =>
                            st.id !== cls.teacher_id && !classCoTeachers.some(ct => ct.teacher_id === st.id))
                        return (
                        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${EDU.border}` }}>
                            <div style={{ fontWeight: 700, color: EDU.navy, fontSize: 13.5, marginBottom: 4 }}>Sharing &amp; Ownership</div>
                            <div style={{ fontSize: 11.5, color: EDU.muted, marginBottom: 10, lineHeight: 1.5 }}>
                                Co-teachers can run this {vocab.courseSingular.toLowerCase()} and see its {vocab.learnerPlural.toLowerCase()}' progress. Transferring ownership hands it to a colleague — it leaves your console.
                            </div>
                            {classCoTeachers.length === 0 ? (
                                <div style={{ fontSize: 12, color: EDU.muted, marginBottom: 8 }}>No co-teachers yet.</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                                    {classCoTeachers.map(ct => (
                                        <div key={ct.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderRadius: 10, background: EDU.cream, fontSize: 12.5 }}>
                                            <span style={{ color: EDU.navy, fontWeight: 700 }}>
                                                {ct.teacher_name || 'Colleague'} · {ct.role === 'viewer' ? 'viewer' : 'co-teacher'}
                                            </span>
                                            <button type="button" onClick={() => sharing.onRemove(cls.id, ct.teacher_id).catch((e: any) => onShowToast(e.message || 'Failed to remove co-teacher', 'error'))} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontWeight: 700, fontSize: 11 }}>Remove</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <select defaultValue="" onChange={async e => {
                                    const v = e.target.value; e.target.selectedIndex = 0
                                    if (!v) return
                                    try { await sharing.onAdd(cls.id, v); onShowToast('Co-teacher added', 'success') }
                                    catch (err: any) { onShowToast(err.message || 'Failed to add co-teacher', 'error') }
                                }} style={{ flex: 1, minWidth: 180, height: 36, padding: '0 10px', borderRadius: 10, border: `1px solid ${EDU.border}`, background: EDU.white, color: EDU.navy, fontSize: 12.5, cursor: 'pointer' }}>
                                    <option value="">Add a co-teacher…</option>
                                    {shareable.map(st => <option key={st.id} value={st.id}>{st.name || st.email}</option>)}
                                </select>
                                <select defaultValue="" onChange={async e => {
                                    const v = e.target.value; e.target.selectedIndex = 0
                                    if (!v) return
                                    const st = sharing.staff.find(s => s.id === v)
                                    if (!window.confirm(`Transfer ownership to ${st?.name || st?.email || 'this colleague'}? The course leaves the current owner's console.`)) return
                                    try { await sharing.onTransfer(cls.id, v, cls.teacher_id!); onShowToast('Ownership transferred', 'success') }
                                    catch (err: any) { onShowToast(err.message || 'Transfer failed', 'error') }
                                }} style={{ flex: 1, minWidth: 180, height: 36, padding: '0 10px', borderRadius: 10, border: `1px solid ${EDU.border}`, background: EDU.white, color: EDU.navy, fontSize: 12.5, cursor: 'pointer' }}>
                                    <option value="">Transfer ownership to…</option>
                                    {shareable.map(st => <option key={st.id} value={st.id}>{st.name || st.email}</option>)}
                                </select>
                            </div>
                        </div>
                        )
                    })()}

                    {/* Community Library Publish Gate — only teacher_created content can be published.
                        ADMINS publish/unpublish (the DB trigger from 04 double-checks the role server-side);
                        TEACHERS get the Suggest action instead — it never flips is_community_published. */}
                    {cls.origin === 'teacher_created' && (
                        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${EDU.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                            <div>
                                <div style={{ fontWeight: 700, color: EDU.navy, fontSize: 13.5 }}>{t('education.publish_to_community_library')}</div>
                                <div style={{ fontSize: 11.5, color: EDU.muted }}>{t('education.make_this_elective_discoverable_and_clonable')}</div>
                                {(cls as any).community_suggested && !cls.is_community_published && (
                                    <div style={{ fontSize: 11.5, color: '#f59e0b', fontWeight: 700, marginTop: 4 }}>
                                        🌱 teacher suggested — review &amp; publish
                                    </div>
                                )}
                            </div>
                            {canPublishCommunity ? (
                                <button
                                    onClick={async () => {
                                        const nextState = !cls.is_community_published
                                        const { error } = await supabase
                                            .from('classes')
                                            .update({ is_community_published: nextState } as any)
                                            .eq('id', cls.id)
                                        if (!error) {
                                            cls.is_community_published = nextState
                                            onShowToast(nextState ? 'Published to Community Library!' : 'Unpublished from Community Library', 'success')
                                        } else {
                                            onShowToast('Failed to update publish status: ' + (error?.message || ''), 'error')
                                        }
                                    }}
                                    style={{
                                        padding: '8px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                                        fontWeight: 700, fontSize: 12,
                                        background: cls.is_community_published ? '#fee2e2' : `${EDU.sage}15`,
                                        color: cls.is_community_published ? '#b91c1c' : EDU.sage,
                                    }}
                                >
                                    {cls.is_community_published ? 'Unpublish' : '🌍 Publish to Community'}
                                </button>
                            ) : (
                                !cls.is_community_published && (
                                    <button
                                        onClick={async () => {
                                            const { error } = await supabase
                                                .from('classes')
                                                .update({ community_suggested: true } as any)
                                                .eq('id', cls.id)
                                            if (!error) {
                                                ;(cls as any).community_suggested = true
                                                onShowToast('Suggested for the Community Library — an admin will review it.', 'success')
                                            } else {
                                                onShowToast('Failed to suggest: ' + (error?.message || ''), 'error')
                                            }
                                        }}
                                        disabled={!!(cls as any).community_suggested}
                                        style={{
                                            padding: '8px 16px', borderRadius: 10, border: 'none',
                                            cursor: (cls as any).community_suggested ? 'default' : 'pointer',
                                            fontWeight: 700, fontSize: 12,
                                            background: (cls as any).community_suggested ? '#f0fdf4' : `${EDU.sage}15`,
                                            color: (cls as any).community_suggested ? '#15803d' : EDU.sage,
                                        }}
                                    >
                                        {(cls as any).community_suggested ? '✅ Suggested' : '🌱 Suggest for Community'}
                                    </button>
                                )
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

// ─── Teacher row ────────────────────────────────────────────────────────────
function TeacherRow({ teacher, onResend, resending }: {
    teacher: EduTeacher
    onResend?: (email: string, name?: string) => Promise<void>
    resending?: boolean
}) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', background: EDU.white, borderRadius: 12, border: `1px solid ${EDU.border}`,
            gap: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: `${EDU.blue}18`, color: EDU.blue,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 900, fontSize: 15, flexShrink: 0,
                }}>
                    {teacher.name ? teacher.name[0].toUpperCase() : '?'}
                </div>
                <div style={{ minWidth: 0 }}>
                    <p style={{ fontWeight: 700, color: EDU.navy, margin: 0, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{teacher.name || 'Pending'}</p>
                    <p style={{ fontSize: 12, color: EDU.muted, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{teacher.email}</p>
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {!teacher.accepted_at && onResend && (
                    <button
                        type="button"
                        onClick={() => onResend(teacher.email, teacher.name || undefined)}
                        disabled={resending}
                        title="Resend invitation email"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px',
                            borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'rgba(56,189,248,0.1)',
                            color: EDU.blue, fontSize: 11, fontWeight: 700, cursor: resending ? 'default' : 'pointer',
                        }}
                    >
                        {resending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                        Resend
                    </button>
                )}
                <span style={{
                    fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em',
                    background: teacher.accepted_at ? `${EDU.green}15` : '#fef3c720',
                    color: teacher.accepted_at ? EDU.green : '#d97706',
                    padding: '3px 10px', borderRadius: 20, flexShrink: 0,
                }}>
                    {teacher.accepted_at ? 'Active' : 'Pending'}
                </span>
            </div>
        </div>
    )
}

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────
export default function EducationAdminPage() {
    const { t } = useTranslation()
    const { user, userProfile, isLoading: authLoading } = useAuth()
    const { isAdmin, isTeacher, isEducationAdmin, loading: adminLoading } = useAdmin()
    const navigate = useNavigate()
    const facilityBranding = useFacilityBranding()
    const resolvedOrgType = (facilityBranding.org_type || (userProfile as any)?.facility_org_type || userProfile?.facility_type) as any
    const vocab = useOrgVocab(resolvedOrgType)

    const {
        hub, classes, students, teachers, progress, loading, error, avgMastery, refetch, myStudentIds,
        inviteTeacher, inviteStudent, createClass, importStudents, fetchStudentLogs,
        setEnrollmentMode, assignStudents, removeStudentEnrollment, getEnrolledStudents,
        publishCourse,
        groups, groupMembers, createGroup, deleteGroup, moveStudentToGroup, bulkAssignGroupToClass,
        coTeachers, facilityStaff, addCoTeacher, removeCoTeacher,
        courseTransfers, requestCourseTransfer, respondToCourseTransfer,
        studentTransfers, requestStudentHandover, respondToStudentHandover,
    } = useEducationAdmin()

    const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null)
    const [resendingEmail, setResendingEmail] = useState<string | null>(null)

    const handleResendTeacher = async (email: string, name?: string) => {
        setResendingEmail(email)
        try {
            await inviteTeacher(email, name, true)
            setToast({ text: `Invitation email resent to ${email}!`, type: 'success' })
        } catch (err: any) {
            setToast({ text: `Failed to resend email: ${err.message}`, type: 'error' })
        } finally {
            setResendingEmail(null)
        }
    }

    const handleResendStudent = async (email: string, name?: string) => {
        setResendingEmail(email)
        try {
            await inviteStudent(email, name, undefined, true)
            setToast({ text: `Invitation email resent to ${email}!`, type: 'success' })
        } catch (err: any) {
            setToast({ text: `Failed to resend email: ${err.message}`, type: 'error' })
        } finally {
            setResendingEmail(null)
        }
    }
    const [modal, setModal] = useState<'teacher' | 'student' | 'class' | null>(null)
    const [groupsModalOpen, setGroupsModalOpen] = useState(false)
    const [newGroupName, setNewGroupName] = useState('')
    const [groupActionLoading, setGroupActionLoading] = useState(false)
    const [handoffOpen, setHandoffOpen] = useState(false)
    const [handoffStudent, setHandoffStudent] = useState<{ id: string; name: string } | null>(null)
    const [handoffTarget, setHandoffTarget] = useState('')
    const [handoffReason, setHandoffReason] = useState('')
    const [handoffLoading, setHandoffLoading] = useState(false)

    const myId = user?.id
    const pendingCourseTransfersForMe = courseTransfers.filter(t => t.status === 'pending' && t.to_teacher_id === myId)
    const pendingStudentTransfersForMe = studentTransfers.filter(t => t.status === 'pending' && t.to_teacher_id === myId)
    const pendingForMeCount = pendingCourseTransfersForMe.length + pendingStudentTransfersForMe.length
    const outgoingCourseTransfers = courseTransfers.filter(t => t.status === 'pending' && t.from_teacher_id === myId)
    const outgoingStudentTransfers = studentTransfers.filter(t => t.status === 'pending' && t.from_teacher_id === myId)
    const [seatReqOpen, setSeatReqOpen] = useState(false)
    const [seatReqCount, setSeatReqCount] = useState(5)
    const [seatReqNote, setSeatReqNote] = useState('')
    const [seatReqLoading, setSeatReqLoading] = useState(false)
    const [invLoading, setInvLoading] = useState(false)
    // Transfer flow: when invite-student returns requires_transfer=true
    const [transferPending, setTransferPending] = useState<{
        email: string; name?: string
        user_id: string; user_name: string
        target_role?: string
        sent?: boolean
    } | null>(null)
    const [transferLoading, setTransferLoading] = useState(false)
    const [inviteHint, setInviteHint] = useState<string | null>(null)
    const [selectedClass, setSelectedClass] = useState<EduClass | null>(null)
    const [mainTab, setMainTab] = useState<'classes' | 'activity' | 'teachers' | 'curriculum'>('classes')
    const { elite: hasEliteAccess, standard: hasStandardAccess } = getEntitlements(userProfile)

    useEffect(() => {
        const classIdParam = new URLSearchParams(window.location.search).get('class')
        if (classIdParam && classes.length > 0) {
            const found = classes.find(c => c.id === classIdParam)
            if (found) setSelectedClass(found)
        }
    }, [classes])

    const showToast = (text: string, type: 'success' | 'error') => {
        setToast({ text, type })
        setTimeout(() => setToast(null), 3500)
    }

    const handleInviteTeacher = async (vals: Record<string, string>) => {
        setInvLoading(true)
        try {
            await inviteTeacher(vals['Email'], vals['Name'])
            setModal(null)
            showToast(vocab.staffInvitedMsg, 'success')
        } catch (err: any) {
            if (err?.requires_transfer && err?.transfer_payload) {
                const p = err.transfer_payload
                setTransferPending({
                    email: vals['Email'],
                    name: p.user_name || vals['Name'],
                    user_id: p.user_id,
                    user_name: p.user_name || vals['Name'],
                    target_role: p.target_role || 'teacher',
                })
                setModal(null)
            } else {
                showToast(err?.message || `Failed to invite ${vocab.staffSingular.toLowerCase()}`, 'error')
            }
        }
        finally { setInvLoading(false) }
    }

    const handleBulkImport = async (studentsToImport: { name: string; email: string }[]) => {
        setInvLoading(true)
        try {
            const results = await importStudents(studentsToImport, selectedClass?.id)
            showToast(`Invited ${results.success} students${results.failed ? `. ${results.failed} failed.` : '.'}`,
                results.failed === 0 ? 'success' : 'error')
        } catch { showToast('Bulk import failed', 'error') }
        finally { setInvLoading(false) }
    }

    const handleInviteStudent = async (email: string, name: string) => {
        setInvLoading(true)
        try {
            await inviteStudent(email, name, selectedClass?.id)
            setModal(null)
            showToast(vocab.learnerInvitedMsg, 'success')
        } catch (err: any) {
            if (err?.requires_transfer && err?.transfer_payload) {
                const p = err.transfer_payload
                setTransferPending({
                    email,
                    name: p.user_name || name,
                    user_id: p.user_id,
                    user_name: p.user_name || name,
                    target_role: 'member',
                })
                setModal(null)
            } else {
                showToast(err.message || `Failed to invite ${vocab.learnerSingular.toLowerCase()}`, 'error')
            }
        }
        finally { setInvLoading(false) }
    }

    const reinviteWithSecondEmail = () => {
        const name = transferPending?.user_name || ''
        const targetRole = transferPending?.target_role || 'member'
        setTransferPending(null)
        setInviteHint(name ? `Enter an alternate email for ${name} to invite them directly without a facility transfer.` : 'Enter an alternate email address to invite this user without a facility transfer.')
        if (targetRole === 'teacher') {
            setModal('teacher')
        } else {
            setModal('student')
        }
    }

    const handleSendTransfer = async () => {
        if (!transferPending) return
        setTransferLoading(true)
        const isMember = transferPending.target_role === 'member' || !transferPending.target_role
        try {
            const { data, error } = await supabase.functions.invoke('request-facility-transfer', {
                body: {
                    user_id: transferPending.user_id,
                    email: transferPending.email,
                    class_id: isMember ? (selectedClass?.id || null) : null,
                    target_role: transferPending.target_role || 'member',
                },
            })
            let parsed: any = data
            if (error) {
                try { parsed = await (error as any).context?.json?.() } catch { /* ignore */ }
                throw new Error(parsed?.error || error.message)
            }
            if (parsed?.error) throw new Error(parsed.error)
            const parsedMsg = parsed?.message || 'Transfer request sent successfully.'
            setTransferPending(prev => prev ? { ...prev, sent: true } : null)
            showToast(parsedMsg, 'success')
        } catch (err: any) {
            showToast(err.message || 'Failed to send transfer request.', 'error')
        } finally {
            setTransferLoading(false)
        }
    }

    // ── Teacher seat request — emails the org's active administrator ──
    const handleRequestSeats = async () => {
        setSeatReqLoading(true)
        try {
            const { data, error } = await supabase.functions.invoke('request-seats', {
                body: { seats: seatReqCount, note: seatReqNote },
            })
            if (error || data?.error) throw new Error((error as any)?.message || data?.error)
            setSeatReqOpen(false)
            setSeatReqNote('')
            showToast(data?.message || 'Seat request sent', 'success')
        } catch (err: any) {
            showToast(err.message || 'Failed to send seat request', 'error')
        } finally { setSeatReqLoading(false) }
    }

    // ── AI generator handoff state (Generate tab → open the generator on the fresh
    //    shell — same contract as the university brand console's createCourse) ──
    const [genOpen, setGenOpen] = useState(false)
    const [genCourseId, setGenCourseId] = useState<string | null>(null)
    const [genCourseName, setGenCourseName] = useState('')
    const [genTargetAge, setGenTargetAge] = useState<number | null>(null)
    const [genUseSearch, setGenUseSearch] = useState(false)

    const handleCreateClass = async (vals: CreateCoursePayload) => {
        setInvLoading(true)
        try {
            await createClass(vals as any)
            setModal(null)
            showToast('Class created', 'success')
            // Generate flow: the shell exists — fetch it and open the generator on it
            // (mirrors UniversityBrandAdminPage.createCourse's generate handoff).
            if (vals?.origin === 'generate') {
                const { data: newCourse } = await supabase
                    .from('classes')
                    .select('id, name')
                    .eq('facility_id', userProfile?.facility_id || facilityBranding?.facility_id)
                    .eq('name', vals.name || 'Untitled course')
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle()
                if (newCourse) {
                    setGenCourseId(newCourse.id)
                    setGenCourseName(newCourse.name || vals.name || '')
                    setGenTargetAge(vals.target_age ?? null)
                    setGenUseSearch(Boolean(vals.use_search))
                    setGenOpen(true)
                }
            }
        } catch { showToast('Failed to create class', 'error') }
        finally { setInvLoading(false) }
    }

    // ── Auth guard ──
    if (authLoading || adminLoading || loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: EDU.cream }}>
                <div style={{ textAlign: 'center' }}>
                    <Loader2 size={36} color={EDU.blue} style={{ margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} />
                    <p style={{ color: EDU.muted }}>{t('education.loading')}</p>
                </div>
            </div>
        )
    }

    if (!user || (!isAdmin && !isTeacher && !isEducationAdmin)) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: EDU.cream, padding: 24 }}>
                <div style={{ textAlign: 'center', maxWidth: 380 }}>
                    <div style={{ width: 64, height: 64, borderRadius: 18, background: `${EDU.blue}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                        <GraduationCap size={32} color={EDU.blue} />
                    </div>
                    <h2 style={{ fontWeight: 800, color: EDU.navy, marginBottom: 10 }}>{t('education.no_education_hub')}</h2>
                    <p style={{ color: EDU.muted, marginBottom: 24 }}>{t('education.you_don_apos_have_an_education')}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <button onClick={() => navigate('/education-signup')} style={{ padding: '13px', background: btnGrad, color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
                            Set Up Education Hub
                        </button>
                        <button onClick={() => navigate('/learn')} style={{ padding: '13px', background: EDU.cream, color: EDU.slate, border: `1px solid ${EDU.border}`, borderRadius: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: EDU.cream }}>
                <div style={{ textAlign: 'center' }}>
                    <AlertTriangle size={36} color="#dc2626" style={{ margin: '0 auto 12px', display: 'block' }} />
                    <p style={{ color: EDU.navy, fontWeight: 700, marginBottom: 8 }}>{error}</p>
                    <button onClick={refetch} style={{ padding: '10px 20px', background: EDU.blue, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer' }}>Retry</button>
                </div>
            </div>
        )
    }

    const facilityRole = userProfile?.facility_role
    const isPrincipal = facilityRole === 'admin' || isAdmin
    const isTeacherRole = facilityRole === 'teacher'
    const hubLabel = facilityBranding.is_branded ? vocab.hubLabel : (hub?.type === 'school' ? 'School Hub' : hub?.type === 'family_edu' ? 'Family Learning Hub' : vocab.hubLabel)

    return (
        <SubdomainBrandingContext.Provider value={{
            ...facilityBranding,
            org_type: resolvedOrgType || facilityBranding.org_type,
            display_name: facilityBranding.is_branded ? facilityBranding.display_name : (hub?.name || facilityBranding.display_name),
        }}>
        <div style={{ minHeight: '100vh', background: EDU.cream }}>

            {/* ── HERO — navy matches app shell, no rogue purple ── */}
            <div style={{ background: heroGrad }}>
                <div style={{ maxWidth: 1040, margin: '0 auto', padding: '20px 16px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                            {(userProfile as any)?.facility_type === 'platform' && isPrincipal && (
                                <button onClick={() => navigate('/platform/setup')} style={{
                                    width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', cursor: 'pointer', color: '#94a3b8', flexShrink: 0,
                                }} title="Back to Setup & Branding"><ArrowLeft size={16} /></button>
                            )}
                            <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {isPrincipal ? <School size={20} color="#0ea5e9" /> : isTeacherRole ? <GraduationCap size={20} color="#0ea5e9" /> : <Users size={20} color="#0ea5e9" />}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <h1 style={{ fontSize: 18, fontWeight: 900, color: '#fff', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {facilityBranding.is_branded ? `${facilityBranding.display_name} — ${vocab.hubLabel}` : (hub?.name || vocab.hubLabel)}
                                </h1>
                                <p style={{ fontSize: 11, fontWeight: 700, color: facilityBranding.is_branded ? facilityBranding.secondary_color : '#0ea5e9', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                                    {facilityBranding.is_branded ? vocab.hubTagline : `${hubLabel} · ${isPrincipal ? vocab.adminTitle : isTeacherRole ? vocab.staffSingular : 'Parent'}`}
                                </p>
                            </div>
                        </div>

                        {/* Action buttons — wrap cleanly on mobile */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                            {facilityBranding.is_branded && <LanguagePicker />}
                            {isPrincipal && (userProfile as any)?.facility_type === 'platform' && (
                                <button onClick={() => navigate('/platform/setup')} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                    background: `${GOLD}18`, color: GOLD,
                                    border: `1px solid ${GOLD}44`, borderRadius: 10,
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <Settings size={14} /> Setup & Branding
                                </button>
                            )}
                            {(isPrincipal || isTeacherRole) && (
                                <button onClick={() => setGroupsModalOpen(true)} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                    background: 'rgba(255,255,255,0.1)', color: '#38bdf8',
                                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                                    position: 'relative',
                                }}>
                                    <Users size={14} /> <span className="sm-inline">Groups</span>
                                    {pendingForMeCount > 0 && (
                                        <span style={{
                                            position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
                                            background: '#f59e0b', color: '#000', fontSize: 10, fontWeight: 900,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>{pendingForMeCount}</span>
                                    )}
                                </button>
                            )}
                            {isTeacherRole && (
                                <button onClick={() => setSeatReqOpen(true)} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                    background: 'rgba(255,255,255,0.1)', color: '#38bdf8',
                                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <Briefcase size={14} /> Request seats
                                </button>
                            )}
                            {/* Enroll Learner / Employee button — available for BOTH admin and teachers so small businesses don't need a middleman */}
                            {(isPrincipal || isTeacherRole) && (
                                <button onClick={() => setModal('student')} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                    background: 'rgba(74, 124, 94, 0.25)', color: '#4ade80',
                                    border: '1px solid rgba(74, 124, 94, 0.45)', borderRadius: 10,
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <UserPlus size={14} /> <span className="sm-inline">{vocab.inviteLearnerTitle}</span>
                                </button>
                            )}
                            {isPrincipal && (
                                <button onClick={() => setModal('teacher')} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                    background: 'rgba(255,255,255,0.1)', color: '#38bdf8',
                                    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <UserPlus size={14} /> <span className="sm-inline">{vocab.inviteStaffTitle}</span>
                                </button>
                            )}
                            {(isPrincipal || isTeacherRole) && (
                                <button onClick={() => setModal('class')} style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                    background: btnGrad, color: '#fff', border: 'none', borderRadius: 10,
                                    fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}>
                                    <Plus size={14} /> {vocab.newCourseLabel}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <svg viewBox="0 0 1440 40" style={{ display: 'block', width: '100%', height: 32 }} preserveAspectRatio="none">
                    <path d="M0,40 C360,0 1080,40 1440,15 L1440,40 Z" fill={EDU.cream} />
                </svg>
            </div>

            {/* ── CONTENT ── */}
            <div style={{ maxWidth: selectedClass ? 'min(1560px, 95vw)' : 1040, margin: '0 auto', padding: '12px clamp(8px, 3vw, 16px) 100px', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

                {/* Stats — 2 col on mobile, 4 col on desktop — placed above tabs as org summary */}
                {!selectedClass && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                        gap: 12, marginBottom: 24,
                    }}>
                        <StatCard icon={<BookOpen size={18} />} label={vocab.coursePlural} value={classes.length} sub="Active" color={EDU.blue} />
                        <StatCard icon={<Users size={18} />} label={vocab.learnerPlural} value={isTeacherRole ? myStudentIds.size : students.length} sub="Enrolled" color={EDU.sage} />
                        {isPrincipal && <StatCard icon={<School size={18} />} label={vocab.staffPlural} value={teachers.length} color={EDU.green} />}
                        <StatCard icon={<Activity size={18} />} label="Mastery" value={`${avgMastery}%`} sub="Avg Score" color={EDU.amber} />
                        <StatCard icon={<BarChart3 size={18} />} label="Seats" value={`${hub?.active_seats ?? 0}/${hub?.total_seats ?? 0}`} color={EDU.blue} />
                    </div>
                )}

                {/* Main Tabs — placed directly above tab content with smooth horizontal scroll and non-truncating labels */}
                {!selectedClass && (
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        marginBottom: 24,
                        overflowX: 'auto',
                        paddingBottom: 6,
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        WebkitOverflowScrolling: 'touch',
                        width: '100%',
                    }}>
                        <button
                            onClick={() => setMainTab('classes')}
                            style={{
                                padding: '10px 18px',
                                borderRadius: 14,
                                border: mainTab === 'classes' ? 'none' : `1px solid ${EDU.border}`,
                                background: mainTab === 'classes' ? EDU.primary : EDU.white,
                                color: mainTab === 'classes' ? 'white' : EDU.navy,
                                fontWeight: 800,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                boxShadow: mainTab === 'classes' ? `0 6px 16px ${EDU.primary}33` : 'none'
                            }}
                        >
                            <BookOpen size={17} /> {vocab.coursePlural}
                        </button>
                        <button
                            onClick={() => setMainTab('activity')}
                            style={{
                                padding: '10px 18px',
                                borderRadius: 14,
                                border: mainTab === 'activity' ? 'none' : `1px solid ${EDU.border}`,
                                background: mainTab === 'activity' ? EDU.primary : EDU.white,
                                color: mainTab === 'activity' ? 'white' : EDU.navy,
                                fontWeight: 800,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                boxShadow: mainTab === 'activity' ? `0 6px 16px ${EDU.primary}33` : 'none'
                            }}
                        >
                            <Activity size={17} /> {vocab.learnerPlural} Activity
                        </button>
                        {isPrincipal && (
                            <button
                                onClick={() => setMainTab('teachers')}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: 14,
                                    border: mainTab === 'teachers' ? 'none' : `1px solid ${EDU.border}`,
                                    background: mainTab === 'teachers' ? EDU.primary : EDU.white,
                                    color: mainTab === 'teachers' ? 'white' : EDU.navy,
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    boxShadow: mainTab === 'teachers' ? `0 6px 16px ${EDU.primary}33` : 'none'
                                }}
                            >
                                <Users size={17} /> {vocab.staffPlural} Roster
                            </button>
                        )}
                        {/* Programs tab = consumer-plan upsell — hidden for platform
                            (white-label) orgs; their plan lives on the org, not the user */}
                        {userProfile?.facility_type !== 'platform' && (
                        <button
                            onClick={() => setMainTab('curriculum')}
                            style={{
                                padding: '10px 18px',
                                borderRadius: 14,
                                border: mainTab === 'curriculum' ? 'none' : `1px solid ${EDU.border}`,
                                background: mainTab === 'curriculum' ? GOLD : EDU.white,
                                color: mainTab === 'curriculum' ? 'white' : EDU.navy,
                                fontWeight: 800,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                boxShadow: mainTab === 'curriculum' ? `0 6px 16px ${GOLD}33` : 'none'
                            }}
                        >
                            <Sparkles size={17} /> Programs
                        </button>
                        )}
                    </div>
                )}

                {/* ── Groups & pending handovers modal ── */}
                {groupsModalOpen && (
                    <Modal title="Groups & Handovers" onClose={() => setGroupsModalOpen(false)} wide>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {pendingForMeCount > 0 && (
                                <div>
                                    <h4 style={{ fontSize: 13, fontWeight: 800, color: EDU.navy, margin: '0 0 8px' }}>Waiting on you</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {pendingCourseTransfersForMe.map(t => (
                                            <div key={t.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 13, color: EDU.navy }}>📚 <b>{t.class_name || 'A course'}</b> is being offered to you</span>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button onClick={async () => { await respondToCourseTransfer(t.id, 'accepted'); showToast('Course accepted', 'success') }} style={{ padding: '5px 10px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Accept</button>
                                                    <button onClick={async () => { await respondToCourseTransfer(t.id, 'declined'); showToast('Course transfer declined', 'success') }} style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.muted, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Decline</button>
                                                </div>
                                            </div>
                                        ))}
                                        {pendingStudentTransfersForMe.map(t => (
                                            <div key={t.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 13, color: EDU.navy }}>🎓 <b>{t.student_name || 'A student'}</b> is being offered to you</span>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button onClick={async () => { await respondToStudentHandover(t.id, 'accepted'); showToast('Student assigned to you', 'success') }} style={{ padding: '5px 10px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Accept</button>
                                                    <button onClick={async () => { await respondToStudentHandover(t.id, 'declined'); showToast('Handover declined', 'success') }} style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.muted, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Decline</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {outgoingCourseTransfers.length + outgoingStudentTransfers.length > 0 && (
                                <div>
                                    <h4 style={{ fontSize: 13, fontWeight: 800, color: EDU.navy, margin: '0 0 8px' }}>Your outgoing requests</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {outgoingCourseTransfers.map(t => (
                                            <div key={t.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 13, color: EDU.navy }}>📚 <b>{t.class_name || 'A course'}</b> — awaiting their response</span>
                                                <button onClick={() => respondToCourseTransfer(t.id, 'cancelled').then(() => showToast('Request cancelled', 'success')).catch((e: any) => showToast(e.message || 'Failed to cancel', 'error'))} style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.muted, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                                            </div>
                                        ))}
                                        {outgoingStudentTransfers.map(t => (
                                            <div key={t.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.25)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ fontSize: 13, color: EDU.navy }}>🎓 <b>{t.student_name || 'A student'}</b> — awaiting their response</span>
                                                <button onClick={() => respondToStudentHandover(t.id, 'cancelled').then(() => showToast('Request cancelled', 'success')).catch((e: any) => showToast(e.message || 'Failed to cancel', 'error'))} style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${EDU.border}`, background: 'transparent', color: EDU.muted, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h4 style={{ fontSize: 13, fontWeight: 800, color: EDU.navy, margin: '0 0 8px' }}>
                                    {isPrincipal ? 'All groups in your hub' : 'Your groups'}
                                </h4>
                                <p style={{ fontSize: 11.5, color: EDU.muted, margin: '0 0 10px', lineHeight: 1.5 }}>
                                    Open by default — every enrolled {vocab.learnerSingular.toLowerCase()} starts with no group. Use groups to organise your own roster (e.g. a class section, or a crew like "Plumbers" / "Mechanics") and to bulk-target a course to one.
                                </p>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                    <input
                                        type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)}
                                        placeholder="e.g. Plumbers, 7A, Science"
                                        style={{ flex: 1, height: 40, padding: '0 12px', borderRadius: 10, border: `2px solid ${EDU.border}`, fontSize: 13, outline: 'none' }}
                                    />
                                    <button
                                        disabled={!newGroupName.trim() || groupActionLoading}
                                        onClick={async () => {
                                            setGroupActionLoading(true)
                                            try { await createGroup(newGroupName); setNewGroupName(''); showToast('Group created', 'success') }
                                            catch (e: any) { showToast(e.message || 'Failed to create group', 'error') }
                                            finally { setGroupActionLoading(false) }
                                        }}
                                        style={{ padding: '0 16px', borderRadius: 10, border: 'none', background: btnGrad, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                                {groups.length === 0 ? (
                                    <p style={{ fontSize: 12.5, color: EDU.muted }}>No groups yet.</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {groups.map(g => (
                                            <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 10, background: EDU.cream }}>
                                                <span style={{ fontSize: 13, fontWeight: 700, color: EDU.navy }}>{g.name}</span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <span style={{ fontSize: 11, color: EDU.muted }}>
                                                        {groupMembers.filter(m => m.group_id === g.id).length} {vocab.learnerPlural.toLowerCase()}
                                                    </span>
                                                    <button
                                                        onClick={async () => {
                                                            if (!window.confirm(`Delete group "${g.name}"?`)) return
                                                            await deleteGroup(g.id)
                                                            showToast('Group deleted', 'success')
                                                        }}
                                                        style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: 2 }}
                                                    ><X size={14} /></button>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Modal>
                )}

                {/* ── Student handoff modal (replaces the window.prompt placeholder) ── */}
                {handoffOpen && handoffStudent && (
                    <Modal title="Hand off student" onClose={() => setHandoffOpen(false)}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div>
                                <div style={{ fontSize: 14, color: EDU.navy, fontWeight: 800, marginBottom: 4 }}>{handoffStudent.name}</div>
                                <p style={{ fontSize: 12, color: EDU.muted, margin: 0, lineHeight: 1.5 }}>
                                    Offer this {vocab.learnerSingular.toLowerCase()} to a colleague, or release them to the admin pool. A colleague must accept before the handover takes effect; releasing to the pool is immediate.
                                </p>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Hand off to</label>
                                <select value={handoffTarget} onChange={e => setHandoffTarget(e.target.value)} style={{ width: '100%', height: 44, padding: '0 12px', borderRadius: 10, border: `2px solid ${EDU.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: EDU.white, color: EDU.navy }}>
                                    <option value="">Release to admin pool (immediate)</option>
                                    {facilityStaff.map(st => (
                                        <option key={st.id} value={st.id}>{st.name || st.email}{st.facility_role === 'admin' ? ' · Administrator' : ' · Course Manager'}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>Reason (optional)</label>
                                <textarea value={handoffReason} onChange={e => setHandoffReason(e.target.value)} rows={2} placeholder="e.g. moving outlets, changing year coordinator" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `2px solid ${EDU.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: EDU.white, color: EDU.navy, resize: 'vertical' }} />
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="button" onClick={() => setHandoffOpen(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, background: EDU.cream, color: EDU.muted, border: `1px solid ${EDU.border}`, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                                <button type="button" disabled={handoffLoading} onClick={async () => {
                                    setHandoffLoading(true)
                                    try {
                                        await requestStudentHandover(handoffStudent.id, handoffTarget || null, handoffReason)
                                        showToast(handoffTarget ? 'Handover requested — awaiting their acceptance' : 'Student released to pool', 'success')
                                        setHandoffOpen(false)
                                    } catch (e: any) {
                                        showToast(e.message || 'Handover failed', 'error')
                                    } finally {
                                        setHandoffLoading(false)
                                    }
                                }} style={{ flex: 2, padding: '12px', borderRadius: 10, background: btnGrad, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: handoffLoading ? 0.6 : 1 }}>
                                    {handoffTarget ? 'Request handover' : 'Release to pool'}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

                {/* ── Teacher seat request modal ── */}
                {seatReqOpen && (
                    <Modal title="Request more seats" onClose={() => setSeatReqOpen(false)}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <p style={{ fontSize: 13, color: EDU.muted, margin: 0, lineHeight: 1.5 }}>
                                Your administrator manages seat purchases. Request additional seats and they&apos;ll be notified by email.
                                Current usage: {hub?.active_seats ?? 0}/{hub?.total_seats ?? 0}.
                            </p>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>{t('education.additional_seats_needed')}</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <button type="button" onClick={() => setSeatReqCount(c => Math.max(1, c - 1))} style={{ width: 40, height: 40, borderRadius: 10, border: `1px solid ${EDU.border}`, background: 'transparent', fontSize: 18, fontWeight: 800, color: EDU.navy, cursor: 'pointer' }}>−</button>
                                    <span style={{ fontSize: 22, fontWeight: 800, color: EDU.navy, minWidth: 36, textAlign: 'center' }}>{seatReqCount}</span>
                                    <button type="button" onClick={() => setSeatReqCount(c => Math.min(500, c + 1))} style={{ width: 40, height: 40, borderRadius: 10, border: `1px solid ${EDU.border}`, background: 'transparent', fontSize: 18, fontWeight: 800, color: EDU.navy, cursor: 'pointer' }}>+</button>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>{t('education.note_optional')}</label>
                                <textarea value={seatReqNote} onChange={e => setSeatReqNote(e.target.value)} rows={3} placeholder="e.g. New intake of 12 trainees starting Monday"
                                    style={{ width: '100%', padding: '12px', borderRadius: 12, border: `2px solid ${EDU.border}`, fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                            </div>
                            <button onClick={handleRequestSeats} disabled={seatReqLoading}
                                style={{ padding: 14, borderRadius: 12, background: btnGrad, color: '#fff', border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: seatReqLoading ? 0.6 : 1 }}>
                                {seatReqLoading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                                Send request
                            </button>
                        </div>
                    </Modal>
                )}

                {/* Class grid or detail */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: selectedClass ? '1fr' : '1fr', // Changed to 1fr to allow for full width content
                    gap: 16,
                }}>
                    {selectedClass ? (
                        <ClassDetail
                            cls={selectedClass}
                            students={students}
                            progress={progress}
                            onBack={() => setSelectedClass(null)}
                            onInviteStudent={handleInviteStudent}
                            onBulkImport={handleBulkImport}
                            inviteLoading={invLoading}
                            onShowToast={showToast}
                            fetchStudentLogs={fetchStudentLogs}
                            navigate={navigate}
                            canPublishCommunity={isPrincipal}
                            onSetEnrollmentMode={setEnrollmentMode}
                            onAssignStudents={assignStudents}
                            onRemoveStudentEnrollment={removeStudentEnrollment}
                            getEnrolledStudents={getEnrolledStudents}
                            onPublishCourse={publishCourse}
                            onResendStudentInvite={handleResendStudent}
                            onGroups={{ list: groups.filter(g => g.owner_id === user?.id || isPrincipal), members: groupMembers }}
                            onMoveStudentToGroup={moveStudentToGroup}
                            onHandoffStudent={(studentId, studentName) => {
                                setHandoffStudent({ id: studentId, name: studentName })
                                setHandoffTarget('')
                                setHandoffReason('')
                                setHandoffOpen(true)
                            }}
                            onSharing={{
                                staff: facilityStaff,
                                coTeachers,
                                onAdd: addCoTeacher,
                                onRemove: removeCoTeacher,
                                onTransfer: (classId, toTeacherId, fromTeacherId, reason) =>
                                    requestCourseTransfer(classId, toTeacherId, reason, fromTeacherId),
                            }}
                        />
                    ) : (
                        <>
                            {/* Main Content Area */}
                            {!selectedClass && mainTab === 'classes' && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
                                    gap: 20,
                                    marginBottom: 40
                                }}>
                                    {classes.length === 0 ? (
                                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '80px 0', background: EDU.white, borderRadius: 32, border: `2px dashed ${EDU.border}` }}>
                                            <BookOpen size={48} color={EDU.muted} style={{ marginBottom: 16, opacity: 0.3 }} />
                                            <p style={{ fontWeight: 800, color: EDU.navy, margin: 0 }}>No {vocab.coursePlural.toLowerCase()} created yet</p>
                                            <p style={{ color: EDU.muted, margin: '8px 0 24px', fontSize: 14 }}>Create your first {vocab.courseSingular.toLowerCase()} to start tracking {vocab.learnerSingular.toLowerCase()} progress.</p>
                                            <button onClick={() => setModal('class')} style={{ padding: '12px 22px', background: EDU.primary, color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}>
                                                <Plus size={17} /> Create First {vocab.courseSingular}
                                            </button>
                                        </div>
                                    ) : (
                                        classes.map(cls => (
                                            <ClassCard key={cls.id} cls={cls} onClick={() => setSelectedClass(cls)} />
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Activity Tab */}
                            {!selectedClass && mainTab === 'activity' && (
                                <div style={{ background: EDU.white, borderRadius: 32, overflow: 'hidden', border: `1px solid ${EDU.border}`, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
                                    <StudentEducationLogs />
                                </div>
                            )}

                            {/* Teachers / Staff Tab */}
                            {!selectedClass && mainTab === 'teachers' && (
                                <div style={{ background: EDU.white, borderRadius: 28, padding: 32, border: `1px solid ${EDU.border}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                                        <div>
                                            <h3 style={{ fontWeight: 900, color: EDU.navy, margin: '0 0 4px', fontSize: 20 }}>{vocab.staffPlural} Roster</h3>
                                            <p style={{ color: EDU.muted, margin: 0, fontSize: 13 }}>Manage assigned {vocab.staffPlural.toLowerCase()} and invite new leaders.</p>
                                        </div>
                                        <button onClick={() => setModal('teacher')} style={{
                                            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px',
                                            background: EDU.primary, color: '#fff', border: 'none', borderRadius: 10,
                                            fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                        }}>
                                            <UserPlus size={15} /> {vocab.inviteStaffTitle}
                                        </button>
                                    </div>
                                    {teachers.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px 20px', background: EDU.cream, borderRadius: 16 }}>
                                            <Users size={32} color={EDU.muted} style={{ margin: '0 auto 12px', display: 'block' }} />
                                            <p style={{ fontWeight: 700, color: EDU.navy, margin: '0 0 6px' }}>No {vocab.staffPlural.toLowerCase()} yet</p>
                                            <p style={{ color: EDU.muted, fontSize: 13, margin: '0 0 16px' }}>Invite your first {vocab.staffSingular.toLowerCase()} to help manage courses and learners.</p>
                                            <button onClick={() => setModal('teacher')} style={{ padding: '10px 18px', background: EDU.primary, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                                                {vocab.inviteStaffTitle}
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {teachers.map(teacher => (
                                                <TeacherRow
                                                    key={teacher.id}
                                                    teacher={teacher}
                                                    onResend={handleResendTeacher}
                                                    resending={resendingEmail === teacher.email}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Curriculum/Programs Tab — retail upsell; tab button is
                                hidden for platform orgs, this guard is defense-in-depth */}
                            {userProfile?.facility_type !== 'platform' && !selectedClass && mainTab === 'curriculum' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                    <div style={{ background: EDU.white, borderRadius: 28, padding: 32, border: `1px solid ${EDU.border}` }}>
                                        <h3 style={{ fontWeight: 900, color: EDU.navy, marginBottom: 8, fontSize: 22 }}>{t('education.your_educational_programs')}</h3>
                                        <p style={{ color: EDU.muted, marginBottom: 24 }}>{t('education.manage_your_active_curriculums_and_explore')}</p>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                                            {/* Standard School Card */}
                                            <div style={{
                                                padding: 24, borderRadius: 24, border: `2px solid ${hasStandardAccess ? EDU.sage : EDU.border}`,
                                                background: hasStandardAccess ? `${EDU.sage}15` : EDU.white, position: 'relative'
                                            }}>
                                                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${EDU.sage}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                                    <BookOpen size={22} color={EDU.sage} />
                                                </div>
                                                <h4 style={{ fontWeight: 800, color: EDU.navy, margin: '0 0 4px' }}>Standard Education</h4>
                                                <p style={{ fontSize: 13, color: EDU.muted, marginBottom: 20 }}>Access to 84 subjects across all difficulty levels.</p>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: hasStandardAccess ? EDU.sage : EDU.muted }}>
                                                        {hasStandardAccess ? '✓ ACTIVE PROGRAM' : 'NOT ACTIVE'}
                                                    </span>
                                                    {!hasStandardAccess && (
                                                        <button onClick={() => navigate('/subscription')} style={{ background: EDU.sage, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Activate</button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Elite Academy Card */}
                                            <div style={{
                                                padding: 24, borderRadius: 24, border: `2px solid ${hasEliteAccess ? GOLD : EDU.border}`,
                                                background: hasEliteAccess ? 'linear-gradient(145deg, rgba(212,175,55,0.1), rgba(15,23,42,0.8))' : EDU.white,
                                                boxShadow: hasEliteAccess ? `0 10px 30px ${GOLD}15` : 'none',
                                                position: 'relative', overflow: 'hidden'
                                            }}>
                                                {hasEliteAccess && (
                                                    <div style={{ position: 'absolute', top: 12, right: 12 }}>
                                                        <Crown size={20} color={GOLD} />
                                                    </div>
                                                )}
                                                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${GOLD}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                                    <Star size={22} color={GOLD} />
                                                </div>
                                                <h4 style={{ fontWeight: 800, color: EDU.navy, margin: '0 0 4px' }}>Elite Academy</h4>
                                                <p style={{ fontSize: 13, color: EDU.muted, marginBottom: 20 }}>{t('education.premium_leadership_etiquette_and_financial_wisdom')}</p>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: hasEliteAccess ? GOLD : EDU.muted }}>
                                                        {hasEliteAccess ? '✓ ACTIVE PROGRAM' : 'PREMIUM UPGRADE'}
                                                    </span>
                                                    {!hasEliteAccess && (
                                                        <button onClick={() => navigate('/subscription')} style={{ background: GOLD, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{t('education.upgrade_to_elite')}</button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {!hasEliteAccess && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            style={{
                                                background: 'linear-gradient(135deg, #1a1b1e, #2c2d33)',
                                                borderRadius: 28, padding: 32, color: '#fff',
                                                display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap',
                                                border: `1px solid ${GOLD}44`, boxShadow: `0 20px 50px rgba(0,0,0,0.3)`
                                            }}
                                        >
                                            <div style={{ flex: 1, minWidth: 280 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: GOLD, marginBottom: 12 }}>
                                                    <Crown size={24} />
                                                    <span style={{ fontWeight: 900, fontSize: 12, letterSpacing: '0.2em' }}>ELITE ACADEMY</span>
                                                </div>
                                                <h3 style={{ fontSize: 28, fontWeight: 900, marginBottom: 12, lineHeight: 1.1 }}>{t('education.unlock_the_billionaire_curriculum')}</h3>
                                                <p style={{ color: '#94a3b8', fontSize: 16, lineHeight: 1.5, marginBottom: 0 }}>
                                                    Give your children the advantage of networking, social grace, global diplomacy, and legacy building. The curriculum they don&apos;t teach in public schools.
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => navigate('/elite')}
                                                style={{
                                                    background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD})`,
                                                    color: '#000', border: 'none', padding: '16px 32px',
                                                    borderRadius: 16, fontWeight: 900, fontSize: 16,
                                                    cursor: 'pointer', boxShadow: `0 10px 30px ${GOLD}44`
                                                }}
                                            >
                                                Learn More
                                            </button>
                                        </motion.div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ── MODALS ── */}
            <AnimatePresence>
                {modal === 'student' && (
                    <Modal title={vocab.inviteLearnerTitle} onClose={() => setModal(null)}>
                        <form onSubmit={async (e) => {
                            e.preventDefault()
                            const form = e.currentTarget
                            const name = (form.elements.namedItem('student_name') as HTMLInputElement)?.value?.trim()
                            const email = (form.elements.namedItem('student_email') as HTMLInputElement)?.value?.trim()
                            const classId = (form.elements.namedItem('student_class') as HTMLSelectElement)?.value || undefined
                            if (!email) return
                            setInvLoading(true)
                            try {
                                await inviteStudent(email, name, classId)
                                setModal(null)
                                showToast(vocab.learnerInvitedMsg, 'success')
                            } catch (err: any) {
                                if (err?.requires_transfer && err?.transfer_payload) {
                                    const p = err.transfer_payload
                                    setTransferPending({
                                        email,
                                        name: p.user_name || name,
                                        user_id: p.user_id,
                                        user_name: p.user_name || name,
                                        target_role: 'member',
                                    })
                                    setModal(null)
                                } else {
                                    showToast(err?.message || `Failed to invite ${vocab.learnerSingular.toLowerCase()}`, 'error')
                                }
                            } finally {
                                setInvLoading(false)
                            }
                        }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {inviteHint && (
                                <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <span>💡 {inviteHint}</span>
                                    <button type="button" onClick={() => setInviteHint(null)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13 }}>✕</button>
                                </div>
                            )}
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                                    {vocab.learnerSingular} Name
                                </label>
                                <input
                                    name="student_name"
                                    type="text"
                                    required
                                    placeholder={`e.g. Alex Morgan`}
                                    style={{ width: '100%', height: 48, padding: '0 14px', borderRadius: 12, border: `2px solid ${EDU.border}`, fontSize: 15, outline: 'none', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', color: EDU.navy }}
                                />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                                    Email Address
                                </label>
                                <input
                                    name="student_email"
                                    type="email"
                                    required
                                    placeholder={`alex@organisation.com`}
                                    style={{ width: '100%', height: 48, padding: '0 14px', borderRadius: 12, border: `2px solid ${EDU.border}`, fontSize: 15, outline: 'none', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', color: EDU.navy }}
                                />
                            </div>
                            {classes.length > 0 && (
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: EDU.muted, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                                        Assign to {vocab.courseSingular} (Optional)
                                    </label>
                                    <select
                                        name="student_class"
                                        defaultValue=""
                                        style={{ width: '100%', height: 48, padding: '0 14px', borderRadius: 12, border: `2px solid ${EDU.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: EDU.white, color: EDU.navy }}
                                    >
                                        <option value="">General Access (All / Org Wide)</option>
                                        {classes.map(cls => (
                                            <option key={cls.id} value={cls.id}>{cleanCourseText(cls.name)}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                                <button
                                    type="button"
                                    onClick={() => setModal(null)}
                                    style={{
                                        flex: 1,
                                        padding: '13px',
                                        borderRadius: 12,
                                        background: EDU.cream,
                                        color: EDU.muted,
                                        border: `1px solid ${EDU.border}`,
                                        fontWeight: 700,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={invLoading}
                                    style={{
                                        flex: 2,
                                        padding: '13px',
                                        borderRadius: 12,
                                        background: btnGrad,
                                        color: '#fff',
                                        border: 'none',
                                        fontWeight: 700,
                                        fontSize: 14,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        opacity: invLoading ? 0.6 : 1,
                                    }}
                                >
                                    {invLoading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={18} />}
                                    {vocab.inviteLearnerTitle}
                                </button>
                            </div>
                        </form>
                    </Modal>
                )}
                {modal === 'teacher' && (
                    <Modal title={vocab.inviteStaffTitle} onClose={() => setModal(null)}>
                        {inviteHint && (
                            <div style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <span>💡 {inviteHint}</span>
                                <button type="button" onClick={() => setInviteHint(null)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13 }}>✕</button>
                            </div>
                        )}
                        <InviteForm
                            label={t('education.send_invite')}
                            fields={[
                                { name: 'Name', placeholder: `${vocab.staffSingular}'s name` },
                                { name: 'Email', placeholder: `${vocab.staffSingular.toLowerCase().replace(/ /g, '.')}@organisation.com`, type: 'email' },
                            ]}
                            onSubmit={handleInviteTeacher}
                            loading={invLoading}
                        />
                    </Modal>
                )}
                {modal === 'class' && (
                    <CreateCourseModal
                        hubName={hub?.name}
                        loading={invLoading}
                        onClose={() => setModal(null)}
                        onCreate={handleCreateClass}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {toast && <Toast text={toast.text} type={toast.type} />}
            </AnimatePresence>

            {/* ── AI generator handoff — opens on the fresh shell after the Generate tab
                  (same contract as the university brand console's generator modal) ── */}
            <AnimatePresence>
                {genOpen && genCourseId && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,12,0.85)', zIndex: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                        onClick={() => setGenOpen(false)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            onClick={e => e.stopPropagation()}
                            style={{ background: '#0f172a', border: `1px solid ${EDU.border}`, borderRadius: 20, width: '100%', maxWidth: 600, maxHeight: '85vh', overflowY: 'auto', padding: 28 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <h3 style={{ fontSize: 18, fontWeight: 800, color: EDU.navy, margin: 0 }}>Syllabus: {genCourseName}</h3>
                                <button onClick={() => setGenOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: EDU.muted }}>
                                    <X size={20} />
                                </button>
                            </div>
                            <SyllabusGenerator
                                classId={genCourseId}
                                targetAge={genTargetAge || 18}
                                subjectName={genCourseName}
                                existingPlan={null}
                                initialUseSearch={genUseSearch}
                                onSyllabusSaved={() => { refetch() }}
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Transfer confirmation overlay ── */}
            {transferPending && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 400,
                        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                    }}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                        style={{
                            background: '#0d1117', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: 20, padding: 32, maxWidth: 480, width: '100%',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                        }}
                    >
                        {transferPending.sent ? (
                            <>
                                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                                    <CheckCircle2 size={24} color="#22c55e" />
                                </div>
                                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>
                                    Transfer Request Sent
                                </h3>
                                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, margin: '0 0 16px' }}>
                                    Transfer request sent to <strong style={{ color: '#fff' }}>{transferPending.user_name}</strong> — they have 7 days to accept.
                                </p>
                                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px', marginBottom: 24 }}>
                                    <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, margin: 0 }}>
                                        A transfer authorization link was emailed to <strong style={{ color: '#fff' }}>{transferPending.email}</strong>. If they accept, their active account moves over immediately. Any history they earned there remains permanently on record with that organisation.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <button
                                        onClick={reinviteWithSecondEmail}
                                        style={{
                                            width: '100%', padding: '13px 0', borderRadius: 12, border: '1px solid rgba(251,191,36,0.3)',
                                            background: 'rgba(251,191,36,0.08)', color: '#fbbf24', fontWeight: 800, fontSize: 14, cursor: 'pointer',
                                        }}
                                    >
                                        Invite with a different email instead
                                    </button>
                                    <button
                                        onClick={() => setTransferPending(null)}
                                        style={{
                                            width: '100%', padding: '13px 0', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)',
                                            background: 'transparent', color: 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                        }}
                                    >
                                        Done
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(251,191,36,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                                    <Mail size={24} color="#f59e0b" />
                                </div>
                                <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>
                                    {transferPending.target_role === 'teacher' ? `${vocab.staffSingular} is in another organisation` : `${vocab.learnerSingular} is in another organisation`}
                                </h3>
                                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', lineHeight: 1.65, margin: '0 0 20px' }}>
                                    <strong style={{ color: '#fff' }}>{transferPending.user_name}</strong> ({transferPending.email}) is already registered with another organisation. {transferPending.target_role === 'teacher' ? vocab.staffPlural : vocab.learnerPlural} can belong to one organisation at a time.
                                </p>
                                <div style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: 0 }}>
                                        <strong style={{ color: '#f59e0b' }}>Send a transfer request?</strong><br />
                                        We'll email <strong style={{ color: '#fff' }}>{transferPending.user_name}</strong> a secure link. <strong style={{ color: '#fff' }}>They decide</strong> whether to move their account to your organisation — their current organisation is not involved in the decision and cannot release or block them. If they accept, their account moves over immediately. If they decline, you can invite them with a different email address instead.
                                    </p>
                                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '10px 0 0', lineHeight: 1.5 }}>
                                        Any history they earned there remains permanently on record with that organisation.
                                    </p>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <button
                                            onClick={handleSendTransfer}
                                            disabled={transferLoading}
                                            style={{
                                                flex: 1, padding: '13px 0', borderRadius: 12, border: 'none',
                                                background: '#0ea5e9', color: '#fff', fontWeight: 800, fontSize: 14,
                                                cursor: transferLoading ? 'not-allowed' : 'pointer',
                                                opacity: transferLoading ? 0.7 : 1,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                            }}
                                        >
                                            {transferLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                                            {transferLoading ? 'Sending…' : 'Send Transfer Request'}
                                        </button>
                                        <button
                                            onClick={() => setTransferPending(null)}
                                            disabled={transferLoading}
                                            style={{
                                                padding: '13px 20px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)',
                                                background: 'transparent', color: 'rgba(255,255,255,0.6)',
                                                fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                            }}
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                    <button
                                        onClick={reinviteWithSecondEmail}
                                        disabled={transferLoading}
                                        style={{
                                            width: '100%', padding: '10px 0', borderRadius: 10, border: '1px dashed rgba(255,255,255,0.15)',
                                            background: 'transparent', color: 'rgba(255,255,255,0.6)',
                                            fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                                        }}
                                    >
                                        Invite with a different email instead
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </div>
        </SubdomainBrandingContext.Provider>
    )
}